/**
 * `InlineCommentExtension` — renders anchored inline-comment highlights
 * as ProseMirror Decorations (NOT persisted marks) and bridges the
 * "comment on this selection" / "open this thread" intents out to React
 * via window CustomEvents.
 *
 * Why a decoration, not a mark (ADR-037): a mark lives inside the doc
 * JSON, so persisting it needs a body write — which needs EDIT
 * permission. In this codebase `canComment` is deliberately independent
 * of `canEdit` (a reviewer / Member can comment on a doc they cannot
 * edit). A persisted mark would silently block reviewers from anchoring
 * comments, the primary inline-comment use case. So the anchor lives on
 * the comment row (`anchor_paragraph_id` + `anchor_text` + `anchor_start`)
 * and the highlight is computed at render time from the live thread set.
 * No body mutation → comment-only reviewers can anchor. Works identically
 * in view + edit modes (same editor instance, `editable` flipped).
 *
 * The plugin gets the live anchor list from React via a `setMeta`
 * transaction (`setInlineCommentAnchors`) and the rail's focused-thread
 * state via another (`setFocusedInlineComment`). Decorations recompute
 * from doc state on every change automatically, so positions track edits;
 * the anchor LIST only changes when thread data changes.
 *
 * Three window events form the React contract:
 *   - `wiki:add-inline-comment` — dispatched on the bubble "Comment"
 *     click and the ⌘⌥M shortcut; carries the captured anchor + the
 *     selection's screen rect so React can float a composer.
 *   - `wiki:open-comment-thread` — dispatched on click inside a
 *     highlight; carries the resolved `threadId`.
 *   - `wiki:inline-anchors-orphaned` — dispatched when the set of
 *     anchors whose quoted text can no longer be located changes;
 *     carries the orphaned thread ids so the rail can render the
 *     orphan state. Deduped so it only fires on actual set changes.
 *
 * Wiki-only: registered behind the `surface === 'wiki'` gate in
 * `extensions.ts`, next to `ParagraphIdExtension` (whose stable `id`
 * attr the anchor resolver depends on). Mirrors the decoration pattern
 * in `empty-paragraph-hint-extension.ts` and the window-event pattern in
 * `link-with-click-to-edit.ts`.
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { type Editor, Extension } from '@tiptap/react'

/** A live anchor handed in from React (one per non-resolved root thread). */
export interface InlineCommentAnchor {
  threadId: string
  anchorParagraphId: string
  anchorText: string
  anchorStart: number
  resolved: boolean
}

/** The descriptor captured from a selection at create time. */
export interface CapturedAnchor {
  anchorParagraphId: string
  anchorText: string
  anchorStart: number
}

/** Plugin key — exported so React can address `setMeta` transactions. */
export const inlineCommentPluginKey = new PluginKey<InlineCommentPluginState>('inlineComment')

/** Block node types a comment may anchor to. */
const COMMENTABLE_BLOCK_TYPES = new Set(['paragraph', 'heading'])

/**
 * Ancestor node types that make a selection non-commentable. A selection
 * sitting inside any of these (code, table cell, panel, expand) can't be
 * anchored — the quoted-text resolver assumes flat prose inside a
 * paragraph/heading.
 */
const NON_COMMENTABLE_ANCESTORS = new Set([
  'codeBlock',
  'table',
  'tableRow',
  'tableCell',
  'tableHeader',
  'infoPanel',
  'notePanel',
  'successPanel',
  'warningPanel',
  'errorPanel',
  'customPanel',
  'expandSection',
])

interface InlineCommentPluginState {
  anchors: InlineCommentAnchor[]
  focusedThreadId: string | null
  /** Thread ids whose anchor text couldn't be located on the last decorate. */
  orphanedThreadIds: string[]
}

interface SetAnchorsMeta {
  type: 'setAnchors'
  anchors: InlineCommentAnchor[]
}
interface SetFocusedMeta {
  type: 'setFocused'
  focusedThreadId: string | null
}
type InlineCommentMeta = SetAnchorsMeta | SetFocusedMeta

/** Window event names — the React contract. */
const ADD_INLINE_COMMENT_EVENT = 'wiki:add-inline-comment'
const OPEN_COMMENT_THREAD_EVENT = 'wiki:open-comment-thread'
const ANCHORS_ORPHANED_EVENT = 'wiki:inline-anchors-orphaned'

/** Detail shape for `wiki:add-inline-comment`. Plain rect, not a live DOMRect. */
export interface AddInlineCommentDetail {
  anchor: CapturedAnchor
  rect: {
    top: number
    bottom: number
    left: number
    right: number
    width: number
    height: number
  }
}

/**
 * Locate the commentable block (paragraph / heading) that contains
 * `pos`, returning the node + its content-start position (the doc
 * position of the block's first inline child). Returns null when the
 * containing block isn't commentable.
 */
function resolveCommentableBlock(
  doc: ProseMirrorNode,
  pos: number,
): { node: ProseMirrorNode; contentStart: number } | null {
  const $pos = doc.resolve(Math.min(Math.max(pos, 0), doc.content.size))
  // Walk from the resolved position outward to its nearest block parent.
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth)
    if (COMMENTABLE_BLOCK_TYPES.has(node.type.name)) {
      // `$pos.start(depth)` is the position just inside the block — i.e.
      // the offset of its first inline child. Paragraph-relative text
      // offsets map to doc positions via `contentStart + offset`.
      return { node, contentStart: $pos.start(depth) }
    }
  }
  return null
}

/**
 * Resolve an anchor to `{ from, to }` doc positions, or null (orphaned).
 *
 * Two tiers, each tried as exact-position first, then text search:
 *   1. blocks whose `id` matches `anchorParagraphId` — the intended home;
 *   2. every commentable block — legacy fallback for a doc whose
 *      paragraph ids were re-minted after the anchor was written (the
 *      duplicate-id repair in `paragraph-id-extension.ts` keeps the FIRST
 *      occurrence's id, so an anchor on a later duplicate loses its id
 *      match but keeps its text).
 *
 * Exact-position-first ACROSS all candidates matters: with duplicate ids
 * the old single pass took the first same-id block and text-searched it,
 * which lit up an earlier occurrence of the same word instead of the one
 * the author selected (sign-off finding).
 */
export function resolveAnchorRange(
  doc: ProseMirrorNode,
  anchor: InlineCommentAnchor,
): { from: number; to: number } | null {
  if (anchor.anchorText.length === 0) return null
  const blocks: Array<{ text: string; contentStart: number; idMatch: boolean }> = []
  doc.descendants((node, pos) => {
    if (!COMMENTABLE_BLOCK_TYPES.has(node.type.name)) return undefined
    // Paragraph content starts at `pos + 1` (one past the node's own
    // open token), so a paragraph-relative offset `o` maps to `pos + 1 + o`.
    blocks.push({
      text: node.textContent,
      contentStart: pos + 1,
      idMatch: node.attrs.id === anchor.anchorParagraphId,
    })
    return false
  })
  const { anchorStart, anchorText } = anchor
  const exactAt = (block: { text: string; contentStart: number }) =>
    block.text.slice(anchorStart, anchorStart + anchorText.length) === anchorText
      ? {
          from: block.contentStart + anchorStart,
          to: block.contentStart + anchorStart + anchorText.length,
        }
      : null
  const searchIn = (block: { text: string; contentStart: number }) => {
    const idx = block.text.indexOf(anchorText)
    return idx === -1
      ? null
      : { from: block.contentStart + idx, to: block.contentStart + idx + anchorText.length }
  }
  for (const tier of [blocks.filter((b) => b.idMatch), blocks]) {
    for (const block of tier) {
      const hit = exactAt(block)
      if (hit) return hit
    }
    for (const block of tier) {
      const hit = searchIn(block)
      if (hit) return hit
    }
  }
  return null
}

/**
 * Build the decoration set for one editor state from the live anchor
 * list. Every located anchor emits a highlight — `data-state` is `open`,
 * `focused` (hovered/selected in the rail — wins even for resolved), or
 * `resolved` (a muted resting style). Only anchors whose quoted text can
 * no longer be located are dropped + collected for the orphaned event.
 *
 * Overlap handling: anchors are sorted by range length ascending so
 * shorter (innermost) ranges decorate last and stay clickable on top;
 * overlapping inline decorations stack their tints naturally.
 */
function buildDecorations(
  doc: ProseMirrorNode,
  anchors: InlineCommentAnchor[],
  focusedThreadId: string | null,
): { set: DecorationSet; orphaned: string[] } {
  // Resolved threads still render a (muted) highlight so the passage stays
  // discoverable + clickable — a resolved thread is a discussion that lived
  // here, not one that never existed. Only truly unresolvable anchors
  // (quoted text edited away) drop out as orphaned.
  const located: Array<{ anchor: InlineCommentAnchor; from: number; to: number }> = []
  const orphaned: string[] = []

  for (const anchor of anchors) {
    const range = resolveAnchorRange(doc, anchor)
    if (!range) {
      orphaned.push(anchor.threadId)
      continue
    }
    located.push({ anchor, from: range.from, to: range.to })
  }

  // Longer ranges first so shorter (innermost) ranges paint last and
  // win click resolution at overlaps.
  located.sort((a, b) => b.to - b.from - (a.to - a.from))

  const decorations: Decoration[] = []
  for (const { anchor, from, to } of located) {
    // Focused wins over resolved so hovering a resolved thread in the rail
    // strengthens its highlight the same way an open one does — resolved
    // is the resting style, focused is the hover/selected style for both.
    const state =
      anchor.threadId === focusedThreadId ? 'focused' : anchor.resolved ? 'resolved' : 'open'
    decorations.push(
      Decoration.inline(from, to, {
        class: 'wiki-anchor-highlight',
        'data-thread-id': anchor.threadId,
        'data-state': state,
      }),
    )
    // Visually-hidden SR marker at the end of the annotated run. The
    // integration agent owns the SR copy; this carries only the count
    // attribute so its mechanism can populate it without a re-render
    // round-trip through this plugin.
    decorations.push(
      Decoration.widget(
        to,
        () => {
          const span = document.createElement('span')
          span.className = 'sr-only'
          span.setAttribute('data-inline-comment-count', '1')
          span.textContent = ''
          return span
        },
        { side: 1 },
      ),
    )
  }

  return { set: DecorationSet.create(doc, decorations), orphaned }
}

/** Shallow equality for the orphaned-thread-id set (order-independent). */
function sameStringSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((id) => setB.has(id))
}

export function buildInlineCommentExtension(): Extension {
  return Extension.create({
    name: 'inlineComment',

    addKeyboardShortcuts() {
      // ⌘⌥M / Ctrl+Alt+M — comment on the current selection. Returns
      // true (consuming the key) only when the selection is commentable;
      // otherwise lets the key fall through to its default.
      return {
        'Mod-Alt-m': () => dispatchAddInlineComment(this.editor),
      }
    },

    addProseMirrorPlugins() {
      // Tracks the last-dispatched orphaned set so the plugin only fires
      // the orphaned event when the set actually changes (avoids a
      // dispatch loop on every doc transaction).
      let lastOrphaned: string[] = []

      return [
        new Plugin<InlineCommentPluginState>({
          key: inlineCommentPluginKey,
          state: {
            init() {
              return { anchors: [], focusedThreadId: null, orphanedThreadIds: [] }
            },
            apply(tr, value) {
              const meta = tr.getMeta(inlineCommentPluginKey) as InlineCommentMeta | undefined
              if (!meta) return value
              if (meta.type === 'setAnchors') {
                return { ...value, anchors: meta.anchors }
              }
              return { ...value, focusedThreadId: meta.focusedThreadId }
            },
          },
          props: {
            decorations(state) {
              const pluginState = inlineCommentPluginKey.getState(state)
              if (!pluginState) return DecorationSet.empty
              const { set, orphaned } = buildDecorations(
                state.doc,
                pluginState.anchors,
                pluginState.focusedThreadId,
              )
              // Dispatch the orphaned event out-of-band (decorations()
              // must stay pure — schedule the side effect after the
              // current tick so we don't mutate during render).
              if (!sameStringSet(orphaned, lastOrphaned)) {
                lastOrphaned = orphaned
                const detail = { orphanedThreadIds: orphaned }
                queueMicrotask(() => {
                  window.dispatchEvent(new CustomEvent(ANCHORS_ORPHANED_EVENT, { detail }))
                })
              }
              return set
            },
            handleClick(view, pos) {
              const pluginState = inlineCommentPluginKey.getState(view.state)
              if (!pluginState || pluginState.anchors.length === 0) return false
              // Resolve the innermost (shortest) anchor range covering
              // `pos`. Build the candidate ranges fresh so click
              // resolution matches what decorations() painted.
              let best: { threadId: string; length: number } | null = null
              for (const anchor of pluginState.anchors) {
                // Resolved anchors are still clickable — they render a muted
                // highlight and clicking re-opens the (resolved) thread.
                const range = resolveAnchorRange(view.state.doc, anchor)
                if (!range) continue
                if (pos < range.from || pos > range.to) continue
                const length = range.to - range.from
                if (!best || length < best.length) {
                  best = { threadId: anchor.threadId, length }
                }
              }
              if (!best) return false
              window.dispatchEvent(
                new CustomEvent(OPEN_COMMENT_THREAD_EVENT, {
                  detail: { threadId: best.threadId },
                }),
              )
              return true
            },
          },
        }),
      ]
    },
  })
}

/** React → plugin: replace the live anchor list. */
export function setInlineCommentAnchors(editor: Editor, anchors: InlineCommentAnchor[]): void {
  const { state, view } = editor
  view.dispatch(
    state.tr.setMeta(inlineCommentPluginKey, {
      type: 'setAnchors',
      anchors,
    } satisfies SetAnchorsMeta),
  )
}

/** React → plugin: set which thread's highlight is "focused" (or null). */
export function setFocusedInlineComment(editor: Editor, threadId: string | null): void {
  const { state, view } = editor
  view.dispatch(
    state.tr.setMeta(inlineCommentPluginKey, {
      type: 'setFocused',
      focusedThreadId: threadId,
    } satisfies SetFocusedMeta),
  )
}

/**
 * Capture the anchor descriptor between two doc positions. Returns null if
 * the range is empty, crosses into a non-paragraph/heading block, or sits
 * inside a codeBlock / table cell / panel / expand node.
 *
 * Multi-paragraph prose selections bind to the START block and clamp the
 * quoted text to that block's end (documented v1 limitation — see the
 * inline-comments plan's "Out of scope"). Shared by the edit-mode PM
 * selection path (`captureSelectionAnchor`) and the view-mode native DOM
 * selection path (`captureAnchorFromDomSelection`).
 */
function captureAnchorBetween(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): CapturedAnchor | null {
  if (from === to) return null
  const lo = Math.min(from, to)
  const hi = Math.max(from, to)

  // Reject if any node in the selected range is a non-commentable
  // container (code / table / panel / expand). nodesBetween visits every
  // node touched by the range, including ancestors.
  let rejected = false
  doc.nodesBetween(lo, hi, (node) => {
    if (NON_COMMENTABLE_ANCESTORS.has(node.type.name)) {
      rejected = true
      return false
    }
    return undefined
  })
  if (rejected) return null

  const block = resolveCommentableBlock(doc, lo)
  if (!block) return null

  const paragraphId = block.node.attrs.id
  if (typeof paragraphId !== 'string' || paragraphId.length === 0) return null

  // Block content runs [contentStart, contentStart + node.content.size).
  // Clamp the range end to the start block's end so a multi-block
  // selection anchors to the first block only (v1 limitation).
  const blockEnd = block.contentStart + block.node.content.size
  const clampedTo = Math.min(hi, blockEnd)
  const anchorStart = lo - block.contentStart
  if (anchorStart < 0) return null

  const anchorText = doc.textBetween(lo, clampedTo, '', '')
  if (anchorText.length === 0) return null

  return { anchorParagraphId: paragraphId, anchorText, anchorStart }
}

/**
 * Capture the anchor descriptor from the editor's current ProseMirror
 * selection (the edit-mode path). Returns null when not commentable.
 */
export function captureSelectionAnchor(editor: Editor): CapturedAnchor | null {
  const { from, to, empty } = editor.state.selection
  if (empty) return null
  return captureAnchorBetween(editor.state.doc, from, to)
}

/**
 * Capture the anchor + screen rect from the browser's NATIVE selection —
 * the view-mode path. In a read-only editor (`editable: false`)
 * ProseMirror does not track a document selection from user gestures, so
 * the edit-mode `state.selection` is empty even while the reader has text
 * visually selected. We read `window.getSelection()` and map its range
 * endpoints to doc positions via `posAtDOM` (a static coordinate mapping
 * that works regardless of `editable`), then reuse the shared capture.
 *
 * Returns the full `AddInlineCommentDetail` (anchor + rect) so the caller
 * can both float a "Comment" button at the selection AND, on click, emit
 * the same event the edit-mode path emits. Returns null when the selection
 * is collapsed, lands outside this editor, or isn't commentable.
 */
export function captureAnchorFromDomSelection(editor: Editor): AddInlineCommentDetail | null {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null

  const range = sel.getRangeAt(0)
  const editorDom = editor.view.dom
  // Both endpoints must sit inside this editor's DOM — otherwise the
  // selection belongs to some other surface (rail, page comments, chrome).
  if (!editorDom.contains(range.startContainer) || !editorDom.contains(range.endContainer)) {
    return null
  }

  let from: number
  let to: number
  try {
    from = editor.view.posAtDOM(range.startContainer, range.startOffset)
    to = editor.view.posAtDOM(range.endContainer, range.endOffset)
  } catch {
    return null
  }

  const anchor = captureAnchorBetween(editor.state.doc, from, to)
  if (!anchor) return null

  const r = range.getBoundingClientRect()
  if (r.width === 0 && r.height === 0) return null

  return {
    anchor,
    rect: {
      top: r.top,
      bottom: r.bottom,
      left: r.left,
      right: r.right,
      width: r.width,
      height: r.height,
    },
  }
}

/**
 * Compute the screen rect of the editor's current selection and dispatch
 * `wiki:add-inline-comment` with the captured anchor. No-op (returns
 * false) when the selection isn't commentable. Shared by the bubble
 * "Comment" button and the ⌘⌥M shortcut so both go through one path.
 */
export function dispatchAddInlineComment(editor: Editor): boolean {
  const anchor = captureSelectionAnchor(editor)
  if (!anchor) return false

  const { from, to } = editor.state.selection
  let rect: AddInlineCommentDetail['rect']
  try {
    const start = editor.view.coordsAtPos(from)
    const end = editor.view.coordsAtPos(to)
    const top = Math.min(start.top, end.top)
    const bottom = Math.max(start.bottom, end.bottom)
    const left = start.left
    const right = end.right
    rect = { top, bottom, left, right, width: right - left, height: bottom - top }
  } catch {
    return false
  }

  emitAddInlineComment({ anchor, rect })
  return true
}

/** Fire `wiki:add-inline-comment` with an already-captured detail. */
export function emitAddInlineComment(detail: AddInlineCommentDetail): void {
  window.dispatchEvent(
    new CustomEvent<AddInlineCommentDetail>(ADD_INLINE_COMMENT_EVENT, { detail }),
  )
}
